import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Stack,
  Title,
  Text,
  Divider,
  Group,
  Badge,
  TextInput,
  Textarea,
  Checkbox,
  MultiSelect,
  JsonInput,
  Button,
  Box,
  ScrollArea,
  Alert,
  Code,
  Tabs,
  Select,
} from '@mantine/core';
import {
  IconX,
  IconCheck,
  IconAlertCircle,
  IconCode,
  IconSettings,
  IconDatabase,
} from '@tabler/icons-react';
import { BaseSchema, TemplateDoc, FieldOverride, BaseField } from '../../../types/geneva-schema';
import { getArrayFieldInfo } from '../../../utils/geneva-schema-helpers';
import { TemplateValidationEditor } from './TemplateValidationEditor';

interface GenevaFieldDrawerProps {
  opened: boolean;
  onClose: () => void;
  record: string | null;
  field: string | null;
  schema: BaseSchema | null;
  template: TemplateDoc | null;
  onFieldOverrideChange: (recordName: string, fieldXmlTag: string, override: FieldOverride) => void;
}

export const GenevaFieldDrawer: React.FC<GenevaFieldDrawerProps> = ({
  opened,
  onClose,
  record,
  field,
  schema,
  template,
  onFieldOverrideChange,
}) => {
  const [localOverride, setLocalOverride] = useState<FieldOverride>({});
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('form');

  // Get the current field and record data
  const currentRecord = useMemo(() => {
    if (!schema || !record) return null;
    return schema.records.find(r => r.record === record) || null;
  }, [schema, record]);

  const currentField = useMemo(() => {
    if (!currentRecord || !field) return null;
    return currentRecord.fields.find(f => f.xmlTag === field) || null;
  }, [currentRecord, field]);

  const existingOverride = useMemo(() => {
    if (!template || !record || !field) return {};
    return template.fieldOverrides[record]?.[field] || {};
  }, [template, record, field]);

  // Update local state when field changes
  useEffect(() => {
    setLocalOverride(existingOverride);
    setJsonError(null);
    setActiveTab('form');
  }, [existingOverride, field, record]);

  const handleSave = () => {
    if (!record || !field) return;

    try {
      onFieldOverrideChange(record, field, localOverride);
      onClose();
    } catch (error) {
      console.error('Failed to save field override:', error);
    }
  };

  const handleReset = () => {
    setLocalOverride({});
    setJsonError(null);
  };

  const handleJsonChange = (value: string) => {
    try {
      const parsed = JSON.parse(value || '{}');
      setLocalOverride(parsed);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const isModified = JSON.stringify(localOverride) !== JSON.stringify(existingOverride);

  if (!currentField || !currentRecord || !opened) {
    return (
      <Drawer
        opened={opened}
        onClose={onClose}
        title="Field Editor"
        position="right"
        size="md"
      >
        <Text c="dimmed">No field selected</Text>
      </Drawer>
    );
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>Field Editor</Text>
          {isModified && <Badge color="orange" size="sm">Modified</Badge>}
        </Group>
      }
      position="right"
      size="lg"
    >
      <ScrollArea style={{ height: 'calc(100vh - 80px)' }}>
        <Stack gap="md">
          {/* Field Info */}
          <Box>
            <Group gap="xs" mb="xs">
              <Text size="lg" fw={600}>{currentField.xmlTag}</Text>
              <Badge variant="light">{currentField.type}</Badge>
              {currentField.required && <Badge color="red" size="sm">Required</Badge>}
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              {currentRecord.record} › {currentField.xmlTag}
            </Text>

            {currentField.description && (
              <Text size="sm" mb="md">
                {currentField.description}
              </Text>
            )}

            {currentField.links && currentField.links.length > 0 && (
              <Box mb="md">
                <Text size="sm" fw={500} mb="xs">Linked Records:</Text>
                <Group gap="xs">
                  {currentField.links.map((link) => (
                    <Badge key={link} variant="outline" size="sm">
                      {link}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Tabs for Form/JSON view */}
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'form')}>
            <Tabs.List>
              <Tabs.Tab value="form" leftSection={<IconSettings size={14} />}>
                Form Editor
              </Tabs.Tab>
              <Tabs.Tab value="json" leftSection={<IconCode size={14} />}>
                JSON Editor
              </Tabs.Tab>
              <Tabs.Tab value="metadata" leftSection={<IconDatabase size={14} />}>
                Metadata
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="form" pt="md">
              <Stack gap="md">
                {/* Basic Overrides */}
                <Group grow>
                  <Checkbox
                    label="Hidden"
                    checked={localOverride.hidden || false}
                    onChange={(e) =>
                      setLocalOverride(prev => ({
                        ...prev,
                        hidden: e.target.checked || undefined
                      }))
                    }
                  />
                  <Checkbox
                    label="Required"
                    checked={localOverride.required ?? currentField.required}
                    onChange={(e) =>
                      setLocalOverride(prev => ({
                        ...prev,
                        required: e.target.checked !== currentField.required ? e.target.checked : undefined
                      }))
                    }
                  />
                  <Checkbox
                    label="Read Only"
                    checked={localOverride.readOnly || false}
                    onChange={(e) =>
                      setLocalOverride(prev => ({
                        ...prev,
                        readOnly: e.target.checked || undefined
                      }))
                    }
                  />
                  <Checkbox
                    label="Per-transaction field"
                    description="Show this field on additional transactions"
                    checked={localOverride.multiTxn || false}
                    disabled={Boolean(getArrayFieldInfo(currentField))}
                    onChange={(e) =>
                      setLocalOverride(prev => ({
                        ...prev,
                        multiTxn: e.target.checked || undefined,
                      }))
                    }
                  />
                </Group>

                {/* Friendly Name */}
                <TextInput
                  label="Friendly Name"
                  placeholder="User-friendly display name for the form"
                  value={localOverride.friendlyName || ''}
                  onChange={(e) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      friendlyName: e.target.value || undefined
                    }))
                  }
                  description="A user-friendly name to display in forms instead of the XML tag"
                />

                {/* Default Value */}
                <TextInput
                  label="Default Value"
                  placeholder="Enter default value"
                  value={String(localOverride.default || '')}
                  onChange={(e) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      default: e.target.value || undefined
                    }))
                  }
                />

                {/* Help Text */}
                <Textarea
                  label="Help Text"
                  placeholder="Additional help text for the field"
                  value={localOverride.help || ''}
                  onChange={(e) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      help: e.target.value || undefined
                    }))
                  }
                  description="Custom help text to guide users when filling out this field"
                  autosize
                  minRows={2}
                  maxRows={4}
                />

                {/* Conditional Required */}
                <TextInput
                  label="Required When (expression)"
                  placeholder="e.g., Quantity > 0"
                  value={localOverride.requiredWhen || ''}
                  onChange={(e) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      requiredWhen: e.target.value || undefined
                    }))
                  }
                />

                {/* Validation Rules */}
                <TemplateValidationEditor
                  validationRules={(localOverride as any).validate}
                  onValidationChange={(rules) => {
                    setLocalOverride(prev => ({ ...prev, validate: rules } as any));
                  }}
                  templates={template?.validationTemplates}
                  fieldName={field || ''}
                  recordFields={currentField ? [currentField] : []}
                  allRecordFields={schema?.records?.flatMap(rec =>
                    rec.fields.map(f => ({ ...f, recordName: rec.record }))
                  ) || []}
                />

                {/* Legacy validation editor - keeping as fallback */}
                <Box style={{ display: 'none' }}>
                  <Text size="sm" fw={500} mb="xs">Validation Rules</Text>
                  <Text size="xs" c="dimmed" mb="xs">Add one or more rules that must evaluate to true. Use the same expression syntax as computed fields.</Text>
                  <Stack gap="sm">
                    {Array.isArray((localOverride as any).validate) ? (localOverride as any).validate.map((rule: any, idx: number) => (
                      <Box key={idx}>
                        <Textarea
                          label={`Expr #${idx + 1}`}
                          placeholder={`// JavaScript expression - can use multiple lines\nreturn Number(Value) > 0;`}
                          value={rule?.expr || ''}
                          onChange={(e) => {
                            const next = [...(localOverride as any).validate];
                            next[idx] = { ...next[idx], expr: e.target.value };
                            setLocalOverride(prev => ({ ...prev, validate: next } as any));
                          }}
                          autosize
                          minRows={6}
                          maxRows={15}
                          styles={{
                            input: {
                              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
                              fontSize: '13px',
                              lineHeight: '1.4',
                              backgroundColor: '#1e1e1e',
                              color: '#d4d4d4',
                              border: '1px solid #404040',
                              borderRadius: '4px',
                              padding: '12px',
                              tabSize: 2,
                              whiteSpace: 'pre',
                              overflowWrap: 'normal',
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                              e.preventDefault();
                              const textarea = e.currentTarget as any;
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              const val = rule?.expr || '';
                              const newValue = val.substring(0, start) + '  ' + val.substring(end);
                              const next = [...(localOverride as any).validate];
                              next[idx] = { ...next[idx], expr: newValue };
                              setLocalOverride(prev => ({ ...prev, validate: next } as any));
                              setTimeout(() => {
                                try { textarea.selectionStart = textarea.selectionEnd = start + 2; } catch { }
                              }, 0);
                            }
                          }}
                        />
                        <Group gap="xs" mt="xs" align="flex-end">
                          <Select
                            label="Level"
                            data={[{ value: 'error', label: 'error' }, { value: 'warning', label: 'warning' }]}
                            value={rule?.level || 'error'}
                            onChange={(val) => {
                              const next = [...(localOverride as any).validate];
                              next[idx] = { ...next[idx], level: (val as any) || undefined };
                              setLocalOverride(prev => ({ ...prev, validate: next } as any));
                            }}
                            style={{ width: 140 }}
                          />
                          <TextInput
                            label="Message"
                            placeholder="Error message"
                            value={rule?.message || ''}
                            onChange={(e) => {
                              const next = [...(localOverride as any).validate];
                              next[idx] = { ...next[idx], message: e.target.value || undefined };
                              setLocalOverride(prev => ({ ...prev, validate: next } as any));
                            }}
                            style={{ flex: 1 }}
                          />
                          <Button
                            size="xs"
                            color="red"
                            variant="subtle"
                            onClick={() => {
                              const next = [...((localOverride as any).validate || [])];
                              next.splice(idx, 1);
                              setLocalOverride(prev => ({ ...prev, validate: next.length ? next : undefined } as any));
                            }}
                          >
                            Remove
                          </Button>
                        </Group>
                      </Box>
                    )) : null}
                    <Group>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          const current = Array.isArray((localOverride as any).validate) ? (localOverride as any).validate : [];
                          const next = [...current, { expr: '' }];
                          setLocalOverride(prev => ({ ...prev, validate: next } as any));
                        }}
                      >
                        Add Rule
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => setLocalOverride(prev => ({ ...prev, validate: undefined } as any))}
                      >
                        Clear Rules
                      </Button>
                    </Group>
                  </Stack>
                </Box>

                {/* Help Text */}
                <Textarea
                  label="Help Text"
                  placeholder="Additional help text for this field"
                  value={localOverride.help || ''}
                  onChange={(e) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      help: e.target.value || undefined
                    }))
                  }
                  autosize
                  minRows={3}
                />

                {/* Options */}
                <MultiSelect
                  label="Fixed Options"
                  description="Define a fixed list of allowed values"
                  placeholder="Add option values"
                  data={localOverride.options || []}
                  value={localOverride.options || []}
                  onChange={(values) =>
                    setLocalOverride(prev => ({
                      ...prev,
                      options: values.length > 0 ? values : undefined
                    }))
                  }
                  searchable
                />

                {/* Lookup Configuration */}
                {localOverride.lookup && (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Lookup Configuration</Text>
                    <Stack gap="xs">
                      <TextInput
                        label="Route"
                        value={localOverride.lookup.route || ''}
                        onChange={(e) =>
                          setLocalOverride(prev => ({
                            ...prev,
                            lookup: {
                              ...prev.lookup!,
                              route: e.target.value
                            }
                          }))
                        }
                      />
                      <Group grow>
                        <TextInput
                          label="Key Field"
                          value={localOverride.lookup.key || ''}
                          onChange={(e) =>
                            setLocalOverride(prev => ({
                              ...prev,
                              lookup: {
                                ...prev.lookup!,
                                key: e.target.value
                              }
                            }))
                          }
                        />
                        <TextInput
                          label="Label Field"
                          value={localOverride.lookup.label || ''}
                          onChange={(e) =>
                            setLocalOverride(prev => ({
                              ...prev,
                              lookup: {
                                ...prev.lookup!,
                                label: e.target.value
                              }
                            }))
                          }
                        />
                      </Group>
                    </Stack>
                  </Box>
                )}

                {/* Quick Actions */}
                <Group>
                  <Button
                    size="sm"
                    variant="light"
                    onClick={() =>
                      setLocalOverride(prev => ({
                        ...prev,
                        lookup: {
                          route: currentField.links?.[0]?.toLowerCase() || '',
                          key: 'id',
                          label: 'name'
                        }
                      }))
                    }
                    disabled={!currentField.links?.length}
                  >
                    Add Lookup
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onClick={() =>
                      setLocalOverride(prev => ({
                        ...prev,
                        computed: { expr: `${currentField.xmlTag}.toLowerCase()` }
                      }))
                    }
                  >
                    Add Computed
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="json" pt="md">
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Edit the field override as JSON. Changes will be reflected in the form editor.
                </Text>

                <JsonInput
                  value={JSON.stringify(localOverride, null, 2)}
                  onChange={handleJsonChange}
                  minRows={10}
                  maxRows={20}
                  validationError={jsonError}
                  formatOnBlur
                  autosize
                />

                {jsonError && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {jsonError}
                  </Alert>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="metadata" pt="md">
              <Stack gap="md">
                <Box>
                  <Text size="sm" fw={500} mb="xs">Base Field Information</Text>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" w={120} c="dimmed">XML Tag:</Text>
                      <Code>{currentField.xmlTag}</Code>
                    </Group>
                    <Group>
                      <Text size="sm" w={120} c="dimmed">Type:</Text>
                      <Code>{currentField.type}</Code>
                    </Group>
                    <Group>
                      <Text size="sm" w={120} c="dimmed">Required:</Text>
                      <Badge color={currentField.required ? 'red' : 'gray'} size="sm">
                        {currentField.required ? 'Yes' : 'No'}
                      </Badge>
                    </Group>
                    {currentField.multiple && (
                      <Group>
                        <Text size="sm" w={120} c="dimmed">Multiple:</Text>
                        <Badge color="blue" size="sm">Yes</Badge>
                      </Group>
                    )}
                  </Stack>
                </Box>

                {currentField.references && (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">References</Text>
                    <Code>{currentField.references}</Code>
                  </Box>
                )}

                {/* {currentField.quickImportColumn && (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Quick Import Column</Text>
                    <Code>{currentField.quickImportColumn}</Code>
                  </Box>
                )} */}
              </Stack>
            </Tabs.Panel>
          </Tabs>

          <Divider />

          {/* Action Buttons */}
          <Group justify="space-between">
            <Group>
              <Button
                variant="outline"
                color="red"
                size="sm"
                onClick={handleReset}
                disabled={Object.keys(localOverride).length === 0}
              >
                Clear Override
              </Button>
            </Group>

            <Group>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isModified || !!jsonError}
                leftSection={<IconCheck size={16} />}
              >
                Save Changes
              </Button>
            </Group>
          </Group>
        </Stack>
      </ScrollArea>
    </Drawer>
  );
}; 
